import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { TranscriptBlock } from '../../transport/api'
import { accountSearch, type AccountSearchResult } from '../../transport/relay'
import { groupTurns, type TurnItem } from '../turns'
import { reportHits, reportSearch, setSearchOlder, useSearch } from './searchStore'
import { findHits, hitItems } from './transcriptSearch'

const NONE: ReadonlySet<TranscriptBlock> = new Set()

/** A pause in typing before the machine is asked — each ask reads the whole conversation. */
const ASK_AFTER_MS = 400
/** Matches navigable per search: the newest ones; `total` still counts every match. */
const ASK_LIMIT = 100
/** Characters of each match kept for the older-than-the-view case (the edge's window around the keyword). */
const ASK_TEXT = 4000

/**
 * The query as the edge takes it: ONE keyword, the whole of what was typed. The Clean
 * View's search has always been a plain substring — `pump fix` finds the lines holding
 * those characters (transcriptSearch.ts) — and the global search's tap lands in this
 * search with the same words, so the two must agree on what matched. Split on spaces,
 * "Clean View" found 119 blocks in avg-devapp where the phrase is in 83 (2026-09-28).
 */
export function keywordsOf(query: string): string[] {
  const q = query.trim()
  return q ? [q] : []
}

/**
 * The agent's own machine, and the window's reach into older blocks — what the search
 * needs to ask the edge instead of the loaded blocks. Absent (a row with no device, a
 * caller that has none), the search is the loaded-blocks one it always was.
 */
export type EdgeSearchTarget = {
  device: string | null
  hash: string | null
  /** Global index of blocks[0] — the transcript window's `meta.from`. */
  from: number
  /** Load older blocks until `index` is in the window; false when it cannot go that far back. */
  reach: (index: number) => Promise<boolean>
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms)
    return () => window.clearTimeout(id)
  }, [value, ms])
  return v
}

/**
 * Clean View's half of the search: the rendered items, which of them hold the keyword,
 * and which one the reader is looking at — plus the side effects that make it usable:
 * the count is reported back to the store, and the current item is scrolled into the
 * reader's view (releasing the stick-to-tail first, or the next poll would yank them
 * back to the bottom while they read).
 *
 * THE WHOLE CONVERSATION — owner, 2026-09-28: 目前的搜尋只是基於前端的 Cache 來做搜尋的,
 * 如果有這樣子直接從 Edge 端可以搜尋到的能力, 那原本前端從 Cache 裡面搜尋的這個功能就可以
 * 替換掉了. With `edge`, the keyword goes to the agent's own machine (POST /account/search
 * with its device and hash), which reads the conversation front to back and answers
 * where every match is. A match above what is loaded is loaded on the way to it
 * (`reach`); one older than the view can load at all is shown by its text. The
 * loaded-blocks search stays as the fallback — an edge from before the route, a machine
 * that did not answer — and says which one ran (the store's `scope`).
 *
 * `items` is the same grouping the view always rendered (groupTurns), memoised here so
 * the hit indices and the render agree on what "item i" is.
 */
export function useCleanSearch(
  blocks: TranscriptBlock[],
  log: { current: HTMLElement | null },
  stick: { current: boolean },
  edge?: EdgeSearchTarget,
): { items: TurnItem[]; hits: ReadonlySet<TranscriptBlock>; currentItem: number } {
  const search = useSearch()
  const items = useMemo(() => groupTurns(blocks), [blocks])

  const query = search.active ? search.query.trim() : ''
  const asked = useDebounced(query, ASK_AFTER_MS)
  const canAsk = !!(edge?.device && edge?.hash)
  const answer = useQuery({
    queryKey: ['conv-search', edge?.device ?? null, edge?.hash ?? null, asked],
    queryFn: ({ signal }) =>
      accountSearch(
        {
          device_id: edge!.device!,
          hash: edge!.hash!,
          keywords: keywordsOf(asked),
          sort: 'time',
          limit: ASK_LIMIT,
          max_text: ASK_TEXT,
        },
        signal,
      ),
    enabled: search.active && canAsk && asked.length > 0,
    staleTime: 30_000,
    retry: false,
  })
  const machine = answer.data?.machines?.[0]
  // In conversation order, oldest first — the order the arrows walk.
  const edgeHits: AccountSearchResult[] | null = useMemo(
    () =>
      machine?.status === 'ok' && answer.data && asked === query
        ? [...answer.data.results].sort((a, b) => a.index - b.index)
        : null,
    [machine?.status, answer.data, asked, query],
  )
  const waiting = canAsk && query.length > 0 && !edgeHits && (asked !== query || answer.isFetching)

  const from = edge?.from ?? 0
  const hits = useMemo(() => {
    if (!search.active) return NONE
    if (edgeHits) {
      const at = new Set(edgeHits.map((r) => r.index))
      const out = new Set<TranscriptBlock>()
      blocks.forEach((b, i) => {
        if (at.has(from + i)) out.add(b)
      })
      return out
    }
    if (waiting) return NONE
    return findHits(blocks, search.query)
  }, [blocks, search.active, search.query, edgeHits, waiting, from])
  const marked = useMemo(() => hitItems(items, hits), [items, hits])

  // Report what was searched and how much was found — the edge's answer or the loaded blocks'.
  useEffect(() => {
    if (!search.active) return
    if (edgeHits) {
      const t = search.target
      const at = t === null ? -1 : edgeHits.findIndex((r) => r.index === t)
      reportSearch({
        count: edgeHits.length,
        total: machine?.total_matches ?? edgeHits.length,
        scope: 'conversation',
        ...(at >= 0 ? { targetAt: at } : {}),
      })
    } else if (waiting) {
      reportSearch({ count: 0, total: 0, scope: 'pending' })
    } else {
      reportHits(marked.length)
    }
  }, [search.active, search.target, edgeHits, waiting, marked, machine?.total_matches])

  // Which item is current: the edge's match, once its block is loaded; or the loaded search's.
  const want = edgeHits && search.current >= 0 ? edgeHits[search.current] : undefined
  const itemOf = useMemo(() => {
    const m = new Map<TranscriptBlock, number>()
    items.forEach((it, i) => {
      if (it.kind === 'said') m.set(it.block, i)
      else for (const b of it.blocks) m.set(b, i)
    })
    return m
  }, [items])
  let currentItem = -1
  if (want) {
    const b = want.index >= from ? blocks[want.index - from] : undefined
    currentItem = b ? (itemOf.get(b) ?? -1) : -1
  } else if (!edgeHits && search.active && search.current >= 0) {
    currentItem = marked[search.current] ?? -1
  }

  // A match above what is loaded: load back to it; one the view cannot reach: show its text.
  const reaching = useRef<number | null>(null)
  const wantIndex = want?.index ?? null
  const needsLoad = want !== undefined && want.index < from
  useEffect(() => {
    if (!needsLoad || wantIndex === null || !edge || reaching.current === wantIndex) return
    reaching.current = wantIndex
    const text = want?.text ?? ''
    const ts = want?.ts
    void edge.reach(wantIndex).then((ok) => {
      if (reaching.current !== wantIndex) return
      reaching.current = null
      if (!ok) setSearchOlder({ index: wantIndex, text, ...(ts !== undefined ? { ts } : {}) })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsLoad, wantIndex])

  useEffect(() => {
    if (currentItem < 0) return
    stick.current = false
    const el = log.current?.querySelector('.hit-current') as HTMLElement | null
    el?.scrollIntoView?.({ block: 'center' })
    // `search.query` is a dependency on purpose: a new keyword that lands on the same
    // item index is still a new place to look.
  }, [currentItem, search.query, log, stick])
  return { items, hits, currentItem }
}
